export const statusMap = {
  Yes: "yes_known",
  "Yes (Unknown)": "yes_unknown",
  No: "no_impact",
  TBD: "tbd",
  "N/A": "n_a",
};

export const impactDisplayMap = {
  yes_known: "Yes",
  yes_unknown: "Yes (Unknown)",
  no_impact: "No",
  tbd: "TBD",
  n_a: "N/A",
};

const normalizeImpact = (value, statusMap) => {
  if (!value) return "tbd";
  if (statusMap && Object.hasOwn(statusMap, value)) return statusMap[value];
  return value;
};

export const buildRfiRequestBody = (projectId, formData, statusMap) => ({
  project_id: projectId,
  subject: formData.rfi?.subject || "",
  question: formData.rfi?.question || "",
  full_number: formData.rfi?.number || "",
  due_date: formData.rfi?.dueDate || "",
  rfi_manager_id: formData.rfi?.rfiManager || null,
  received_from_id: formData.rfi?.receivedFrom || null,
  assignee_ids: (formData.rfi?.assignees || []).map((a) => a.id),
  required_assignee_ids: formData.rfi?.requiredAssigneeIds || [],
  responsible_contractor_id: (() => {
    const contractor = formData.rfi?.responsibleContractor;
    if (!contractor) return null;
    return typeof contractor === "object" ? contractor.id : contractor;
  })(),
  specification_section_id: formData.rfi?.specification || null,
  project_stage_id: formData.rfi?.rfiStage || null,
  location_id: formData.rfi?.location || null,
  cost_code_id: formData.rfi?.costCode || null,
  drawing_number: formData.rfi?.drawingNumber || "",
  schedule_impact: {
    status: normalizeImpact(formData.rfi?.scheduleImpact, statusMap),
    value: Number.parseInt(formData.rfi?.scheduleImpactDetails, 10) || 0,
  },
  cost_impact: {
    status: normalizeImpact(formData.rfi?.costImpact, statusMap),
    value: Number.parseFloat(formData.rfi?.costImpactDetails) || 0,
  },
  reference: formData.rfi?.reference || "",
  private: formData.rfi?.private || false,
  draft: formData.rfi?.draft ?? false,
});

export const eventTypeDisplayMap = {
  tbd: "To Be Determined",
  allowance: "Allowance",
  contingency: "Contingency",
  owner_change: "Owner Change",
  transfer: "Transfer",
};

export const scopeDisplayMap = {
  tbd: "To Be Determined",
  in_scope: "In Scope",
  out_of_scope: "Out of Scope",
};

const buildBudgetCodeFields = (item, out) => {
  if (item.budgetCode || item.budgetCodeId) {
    out.budget_code = Number(item.budgetCode ?? item.budgetCodeId);
  }
  if (item.costCodeId) out.cost_code_id = Number(item.costCodeId);
};

const buildBasicFields = (item, out) => {
  if (item.description) out.description = item.description;
  if (item.unitOfMeasure) out.uom = item.unitOfMeasure;
};

const buildVendorAndContractFields = (item, out) => {
  if (item.vendor || item.vendorId) out.proposed_vendor_id = Number(item.vendor ?? item.vendorId);
  if (item.contract || item.contractId)
    out.proposed_contract_id = Number(item.contract ?? item.contractId);
};

const buildCostFields = (item, out) => {
  const qty = item.costQty != null && item.costQty !== "" ? Number(item.costQty) : null;
  const unit =
    item.costUnitCost != null && item.costUnitCost !== "" ? Number(item.costUnitCost) : null;
  if (qty != null) out.estimated_cost_quantity = qty;
  if (unit != null) out.estimated_cost_unit_cost = unit;
  if (qty != null && unit != null) out.estimated_cost_amount = String(qty * unit);
};

const buildLineItem = (item) => {
  const out = {};
  buildBudgetCodeFields(item, out);
  buildBasicFields(item, out);
  buildVendorAndContractFields(item, out);
  buildCostFields(item, out);

  if (item?.costROM?.method) {
    out.estimated_cost_calculation_strategy = item.costROM.method;
  }

  if (item.commitmentLineItemId) {
    out.commitment_contract_line_item_id = Number(item.commitmentLineItemId);
  }

  return out;
};

export const buildChangeEventRequestBody = (projectId, formData) => {
  const ce = formData?.changeEvent || {};
  const rows = formData?.lineItems || [];

  const change_event_line_items_attributes = (rows || []).map(buildLineItem);

  const body = {
    project_id: Number(projectId),
    number: ce.number || "",
    title: ce.title || "",
    description: ce.description || "",
    change_type: ce.type ? { id: Number(ce.type) } : null,
    status: ce.status ? { id: Number(ce.status) } : null,
    change_reason: ce.changeReason ? { id: Number(ce.changeReason) } : null,
    scope: ce.scope || "tbd",
    line_item_revenue_source: ce.revenueSource || null,
    prime_contract: ce.primeContract ? { id: Number(ce.primeContract) } : null,
    ...(ce.origin && {
      event_origin: {
        origin_id: Number(ce.origin),
        origin_type: ce.originType || "rfi",
      },
    }),
    change_event_line_items_attributes,
  };

  return body;
};

export const buildSubcontractRequestBody = (projectId, formData) => {
  const rows = formData.commitments?.subcontractScheduleOfValues || [];

  const line_items = (rows || [])
    .map((item) => {
      const out = {};

      if (item.budgetCodeId) {
        out.budget_code = Number(item.budgetCodeId);
      }

      if (item.description) out.description = item.description;
      if (item.amount != null && item.amount !== "") out.amount = Number(item.amount);

      return out;
    })
    .filter((item) => Object.keys(item).length > 0);

  return {
    project_id: projectId,
    work_order_contract: {
      number: formData.commitments?.contractNumber || "",
      vendor_id: formData.commitments?.contractCompany
        ? Number.parseInt(formData.commitments.contractCompany, 10)
        : undefined,
      title: formData.commitments?.title || "",
      status: formData.commitments?.status || "Draft",
      executed: formData.commitments?.executed || false,
      retainage_percent: Number.parseFloat(formData.commitments?.defaultRetainage) || 0,
      description: formData.commitments?.contractDescription || "",
      private: formData.commitments?.private || false,
      show_line_items_to_non_admins: formData.commitments?.allow || false,
      contract_date: formData.commitments?.contractDate || null,
      contract_start_date: formData.commitments?.startDate || null,
      contract_estimated_completion_date: formData.commitments?.estimatedDate || null,
      actual_completion_date: formData.commitments?.actualDate || null,
      signed_contract_received_date: formData.commitments?.receivedDate || null,
      issued_on_date: formData.commitments?.issuedDate || null,
      line_items: line_items.length > 0 ? line_items : undefined,
    },
  };
};

export const buildPurchaseOrderRequestBody = (projectId, formData) => {
  const rows = formData.commitments?.purchaseOrderScheduleOfValues || [];

  const line_items = (rows || [])
    .map((item) => {
      const out = {};

      if (item.budgetCodeId) {
        out.budget_code = Number(item.budgetCodeId);
      }

      if (item.description) out.description = item.description;
      if (item.qty != null && item.qty !== "") out.quantity = Number(item.qty);
      if (item.uom) out.unit_of_measure = item.uom;
      if (item.unitCost != null && item.unitCost !== "") out.unit_cost = Number(item.unitCost);
      if (item.amount != null && item.amount !== "") out.amount = Number(item.amount);

      return out;
    })
    .filter((item) => Object.keys(item).length > 0);

  return {
    project_id: projectId,
    number: formData.commitments?.purchaseContractNumber || "",
    vendor_id: formData.commitments?.purchaseContractCompany || null,
    title: formData.commitments?.purchaseTitle || "",
    status: formData.commitments?.purchaseStatus || "Draft",
    executed: formData.commitments?.purchaseExecuted || false,
    retainage_percent: Number.parseFloat(formData.commitments?.purchaseDefaultRetainage) || 0,
    assignee_id: formData.commitments?.purchaseAssignedTo || null,
    bill_to_address: formData.commitments?.billTo || "",
    payment_terms: formData.commitments?.paymentTerms || "",
    ship_to_address: formData.commitments?.shipTo || "",
    ship_via: formData.commitments?.shipVia || "",
    description: formData.commitments?.purchaseDescription || "",
    contract_date: formData.commitments?.purchaseContractDate || null,
    delivery_date: formData.commitments?.purchaseDeliveryDate || null,
    signed_contract_received_date: formData.commitments?.purchaseReceivedDate || null,
    issued_on_date: formData.commitments?.purchaseIssuedDate || null,
    private: formData.commitments?.private || false,
    group: formData.commitments?.group || null,
    show_line_items_to_non_admins: formData.commitments?.purchaseAllow || false,
    line_items: line_items.length > 0 ? line_items : undefined,
  };
};
